<template>
  <div class="initialize-community-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('initializeCommunityManage.page.title')" :subtitle="$t('initializeCommunityManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.communityId" clearable class="w-full"
            :placeholder="$t('initializeCommunityManage.search.communityId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable class="w-full"
            :placeholder="$t('initializeCommunityManage.search.name')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('initializeCommunityManage.list.title') }}</span>
          <el-tooltip :content="$t('initializeCommunityManage.page.tip')" placement="top">
            <i class="el-icon-info ds-panel__info" />
          </el-tooltip>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('initializeCommunityManage.page.empty')" @retry="getList">
        <el-table-column prop="communityId" :label="$t('initializeCommunityManage.table.communityId')" align="center"
          min-width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.communityId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('initializeCommunityManage.table.name')" align="center" min-width="140" />
        <el-table-column prop="nearbyLandmarks" :label="$t('initializeCommunityManage.table.nearbyLandmarks')"
          align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.nearbyLandmarks || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="cityName" :label="$t('initializeCommunityManage.table.cityCode')" align="center" width="120" />
        <el-table-column prop="stateName" :label="$t('initializeCommunityManage.table.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleInitialize(scope.row)">
              {{ $t('initializeCommunityManage.button.initialize') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('initializeCommunityManage.page.summary') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 操作结果提示 -->
    <el-alert v-if="msgData" class="ds-result-alert" type="warning" :closable="true" show-icon>
      {{ msgData }}
    </el-alert>

    <choose-initialize-community :visible.sync="dialogVisible" :community-info="currentCommunity"
      @confirm="handleConfirmInitialize" />
  </div>
</template>

<script>
import { listCommunitys, initializeCommunity } from '@/api/community/initializeCommunityManageApi'
import ChooseInitializeCommunity from '@/components/community/ChooseInitializeCommunity'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'InitializeCommunityManageList',
  components: {
    ChooseInitializeCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        communityId: '',
        name: '',
        cityCode: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      msgData: '',
      dialogVisible: false,
      currentCommunity: {}
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.searchForm
        }
        const { communitys, total } = await listCommunitys(params)
        this.tableData = Array.isArray(communitys) ? communitys : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取小区数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('initializeCommunityManage.page.loadFailed')
        this.$message.error(this.$t('initializeCommunityManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        communityId: '',
        name: '',
        cityCode: ''
      }
      this.pagination.current = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleInitialize(row) {
      this.currentCommunity = { ...row }
      this.dialogVisible = true
    },
    async handleConfirmInitialize(data) {
      try {
        const res = await initializeCommunity(data)
        this.msgData = res.data
        this.$message.success(this.$t('common.operationSuccess'))
        this.getList()
      } catch (error) {
        this.$message.error(error.message)
      }
    },
    /**
     * 小区状态语义类型（s_community.state）
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'community',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.initialize-community-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__info {
    color: var(--ds-gray-400);
    cursor: pointer;
    transition: color var(--ds-transition-fast);

    &:hover {
      color: var(--ds-primary);
    }
  }
}

/* 操作结果提示 */
.ds-result-alert {
  margin-bottom: var(--ds-spacing-md);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
